import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";

const CATEGORIES = [
  { key: "deliverables", name: "Deliverables" },
  { key: "skills", name: "Skills" },
  { key: "culture", name: "Culture" },
];

const gradeColors = {
  Exceptional: { bg: "bg-purple-500/20", text: "text-purple-600", border: "border-purple-500" },
  Advanced: { bg: "bg-teal-500/20", text: "text-teal-600", border: "border-teal-500" },
  Intermediate: { bg: "bg-blue-500/20", text: "text-blue-600", border: "border-blue-500" },
  Basic: { bg: "bg-yellow-500/20", text: "text-yellow-600", border: "border-yellow-500" },
  Poor: { bg: "bg-red-500/20", text: "text-red-600", border: "border-red-500" },
};

function getGradeInfo(grade) {
  const info = {
    Exceptional: { emoji: "🏆", color: "purple" },
    Advanced: { emoji: "⭐", color: "teal" },
    Intermediate: { emoji: "✓", color: "blue" },
    Basic: { emoji: "⚠️", color: "yellow" },
    Poor: { emoji: "🚩", color: "red" },
  };
  return info[grade] || info.Intermediate;
}

export default function Overview({ cycle }) {
  const navigate = useNavigate();
  const { businessUnit } = useSelector((state) => state.Auth);
  const business_unit_id = businessUnit?._id;
  const quarter = cycle?.quarter;
  const year = cycle?.year;
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!business_unit_id || !quarter || !year) {
      setTeam([]);
      return;
    }
    fetchTeamReviews();
  }, [business_unit_id, quarter, year]);

  async function fetchTeamReviews() {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/performance_review/search", { business_unit_id, quarter, year });

      if (!ok) return;

      const mapped = data.map((r) => {
        const hasScores = r.overall_score > 0 || r.status === "completed";
        const gradeInfo = hasScores ? getGradeInfo(r.overall_grade) : { emoji: "📋", color: "gray" };
        return {
          id: r._id,
          name: r.user_name,
          avatar: r.user_avatar,
          seniority: r.seniority,
          overall: r.overall_score || 0,
          deliverables: r.deliverables,
          skills: r.skills,
          culture: r.culture,
          finalGrade: hasScores ? (r.overall_grade || "Basic") : null,
          gradeEmoji: gradeInfo.emoji,
          talentBar: r.talent_bar_comparison || null,
          strengths: r.strengths || [],
          developmentAreas: r.development_areas || [],
          status: r.status,
          isAssessed: hasScores,
        };
      });

      setTeam(mapped);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load team reviews");
    } finally {
      setLoading(false);
    }
  }

  const assessedTeam = team.filter((m) => m.isAssessed);
  const unassessedCount = team.length - assessedTeam.length;

  const distribution = {
    Poor: { count: 0, percent: 0, target: 5 },
    Basic: { count: 0, percent: 0, target: 15 },
    Intermediate: { count: 0, percent: 0, target: 50 },
    Advanced: { count: 0, percent: 0, target: 25 },
    Exceptional: { count: 0, percent: 0, target: 5 },
  };

  assessedTeam.forEach((m) => {
    if (distribution[m.finalGrade]) {
      distribution[m.finalGrade].count++;
    }
  });

  Object.keys(distribution).forEach((grade) => {
    distribution[grade].percent = assessedTeam.length > 0
      ? Math.round((distribution[grade].count / assessedTeam.length) * 100)
      : 0;
  });

  const nips = assessedTeam.length > 0
    ? ((assessedTeam.reduce((sum, m) => sum + m.overall, 0) / assessedTeam.length / 5) * 100).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  function NIPSGauge({ value }) {
    const radius = 80;
    const stroke = 12;
    const normalizedRadius = radius - stroke / 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (value / 100) * circumference;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
          <circle stroke="#e5e7eb" fill="transparent" strokeWidth={stroke} r={normalizedRadius} cx={radius} cy={radius} />
          <circle
            stroke="#2563eb"
            fill="transparent"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference + " " + circumference}
            style={{ strokeDashoffset }}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className="text-xs text-gray-500 uppercase tracking-wide">NIPS</span>
          <span className="text-3xl font-bold text-gray-900">{value}%</span>
        </div>
      </div>
    );
  }

  function DistributionChart({ distribution }) {
    const grades = ["Poor", "Basic", "Intermediate", "Advanced", "Exceptional"];
    const maxPercent = Math.max(...Object.values(distribution).map((d) => Math.max(d.percent, d.target)));

    return (
      <div className="space-y-3">
        <div className="flex justify-end gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 border-2 border-dashed border-gray-400 rounded-sm"></div>
            <span className="text-gray-600">Target</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
            <span className="text-gray-600">Actual</span>
          </div>
        </div>
        <div className="flex items-end gap-2 h-32">
          {grades.map((grade) => {
            const { count, percent, target } = distribution[grade];
            const barHeight = (percent / maxPercent) * 100;
            const targetHeight = (target / maxPercent) * 100;

            return (
              <div key={grade} className="flex-1 flex flex-col items-center">
                <div className="relative w-full h-32 flex items-end justify-center">
                  <div className="absolute w-full border-t-2 border-dashed border-gray-400" style={{ bottom: `${targetHeight}%` }}>
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs text-gray-600">{target}%</span>
                  </div>
                  <div className={`w-full max-w-[60px] rounded-t-md ${gradeColors[grade].bg} transition-all duration-500`} style={{ height: `${barHeight}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2">
          {grades.map((grade) => {
            const { count, percent } = distribution[grade];
            return (
              <div key={grade} className="flex-1 text-center">
                <p className="text-xs text-gray-600">{grade}</p>
                <p className="text-xs text-gray-500">{count} ({percent}%)</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function GradeBadge({ grade }) {
    const colors = gradeColors[grade] || gradeColors.Basic;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
        {grade}
      </span>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              Performance
              <span className="text-gray-500 cursor-help" title="Net Individual Performance Score">ⓘ</span>
            </h3>
          </div>
          <div className="flex justify-center">
            <NIPSGauge value={nips} />
          </div>
          <p className="text-center text-sm text-gray-600 mt-4">
            <span className="text-blue-600">NIPS</span> (Net Individual Performance Score)
          </p>
          {unassessedCount > 0 && (
            <p className="text-center text-xs text-gray-500 mt-2">
              {assessedTeam.length}/{team.length} assessed · <span className="text-yellow-600">{unassessedCount} pending</span>
            </p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              Target distribution
              <span className="text-gray-500 cursor-help" title="Performance grade distribution">ⓘ</span>
            </h3>
          </div>
          <DistributionChart distribution={distribution} />
        </div>
      </div>

      {/* Team Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                Name · {team.length}
              </th>
              {CATEGORIES.map((cat) => (
                <th key={cat.key} className="text-center px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  {cat.name}
                </th>
              ))}
              <th className="text-center px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Overall</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Grade</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-600 uppercase tracking-wider">Talent Bar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {team.map((member) => (
              <tr
                key={member.id}
                className={`hover:bg-gray-50 cursor-pointer transition-colors ${!member.isAssessed ? "opacity-60" : ""}`}
                onClick={() => navigate(`/performance/assess/${member.id}`)}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${member.isAssessed ? "bg-gradient-to-br from-blue-500 to-blue-600" : "bg-gray-400"}`}>
                        {member.name?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{member.name}</p>
                      <p className="text-xs text-gray-500">{member.seniority}</p>
                    </div>
                  </div>
                </td>
                {member.isAssessed ? (
                  <>
                    {CATEGORIES.map((cat) => {
                      const avg = member[cat.key]?.average;
                      return (
                        <td key={cat.key} className="px-4 py-3 text-center">
                          <span className="text-sm text-gray-700">{avg?.toFixed(1) ?? "—"}</span>
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-center">
                      <span className={`text-lg font-bold ${gradeColors[member.finalGrade]?.text || "text-gray-700"}`}>
                        {member.overall?.toFixed(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <GradeBadge grade={member.finalGrade} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${member.talentBar?.includes("Below") ? "text-yellow-600" : "text-blue-600"}`}>
                        {member.talentBar}
                      </span>
                    </td>
                  </>
                ) : (
                  <td colSpan={CATEGORIES.length + 3} className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      <span>📋</span>
                      Not assessed yet
                      <span className="text-blue-600 text-xs">→ Click to assess</span>
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
